import cn from 'classnames';
import { forwardRef } from 'react';

import { useFieldId } from '../../../utils';
import { BaseField, FieldLabel, Select } from '../../internal';

import type { ForwardedRef, ReactElement } from 'react';

import type { SelectFieldComponentType, SelectFieldProps } from './select-field.types';

const SelectField = forwardRef(
  <OptionValue extends NonNullable<unknown>, Multiple extends boolean = false>(
    props: SelectFieldProps<OptionValue, Multiple>,
    forwardedRef: ForwardedRef<HTMLElement>,
  ): ReactElement => {
    const {
      children,
      className,
      defaultValue,
      disabled,
      error,
      icon,
      id,
      label,
      multiple,
      name,
      onChange,
      placeholder,
      readOnly,
      required,
      tooltip,
      value,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const controlId = useFieldId(undefined, name ?? 'rbui-select-field');

    const selectProps = {
      defaultValue,
      disabled,
      error,
      icon,
      id: controlId,
      multiple,
      name,
      onChange,
      placeholder,
      readOnly,
      required,
      value,
    };

    const fieldLabel = label && (
      <FieldLabel htmlFor={controlId} required={required} tooltip={tooltip}>
        {label === ' ' ? <span>&nbsp;</span> : label}
      </FieldLabel>
    );

    return (
      <BaseField
        className={cn(className, 'rbui-select-field')}
        control={
          <Select {...selectProps} ref={forwardedRef}>
            {children}
          </Select>
        }
        error={error}
        label={fieldLabel}
        {...rest}
      />
    );
  },
);

SelectField.displayName = 'SelectField';

/**
 * The `SelectField` component is meant to optimize user interaction and experience.
 * It is a customizable dropdown list that allows users to choose one or more options from a list.
 * Its primary function is to enhance and simplify data input, minimizing user effort
 * in navigation while avoiding incorrect entries.
 *
 * ### Usage
 *
 * ```tsx
 * import { SelectField, SelectOption } from '@bloomreach/react-banana-ui';
 * import React from 'react';
 *
 * export default function MyCustomSingleSelectField() {
 *   return (
 *     <SelectField
 *       label="List of fruits"
 *       placeholder="Select a fruit"
 *       onChange={(event, value) => console.log(value)}
 *     >
 *       <SelectOption value="apple">Apple</SelectOption>
 *       <SelectOption value="banana">Banana</SelectOption>
 *       <SelectOption value="orange">Orange</SelectOption>
 *       <SelectOption value="grapes">Grapes</SelectOption>
 *       <SelectOption value="strawberries">Strawberries</SelectOption>
 *       <SelectOption value="blueberries">Blueberries</SelectOption>
 *       <SelectOption value="pineapple">Pineapple</SelectOption>
 *     </SelectField>
 *   );
 * }
 * ```
 */
export default SelectField as SelectFieldComponentType;
